<template>
    <VJoyTemplate
        ref="templateElementRef"
        :loading="isStepLoading || isSaving || isTransitionInProgress"
        :data-testid="props.dataTestid"
        class="freelancer-signup-step"
    >
        <template #template-title>
            <div
                class="signup-title"
                :class="{'signup-title--with-subtitle': $slots['freelancer-signup-step-subtitle'], [props.titleColor]: true}"
                :data-testid="`${props.dataTestid}--title`"
            >
                <slot name="freelancer-signup-step-title" />
            </div>
        </template>
        <template v-if="$slots['freelancer-signup-step-subtitle'] || $slots['freelancer-signup-step-intro']" #template-subtitle>
            <div v-if="$slots['freelancer-signup-step-subtitle']" class="signup-subtitle" :data-testid="`${props.dataTestid}--subtitle`">
                <slot name="freelancer-signup-step-subtitle" />
            </div>
            <div v-if="$slots['freelancer-signup-step-intro']" :data-testid="`${props.dataTestid}--intro`">
                <slot name="freelancer-signup-step-intro" />
            </div>
        </template>
        <template #template-main>
            <div class="freelancer-signup-step__main">
                <slot v-if="$slots['freelancer-signup-step-main']" name="freelancer-signup-step-main" />
                <div v-else class="freelancer-signup-step__wrapper">
                    <slot name="freelancer-signup-step-main-left" />
                    <slot name="freelancer-signup-step-main-right" />
                </div>
                <VJoyFunnelFooter>
                    <template #footer-left>
                        <slot v-if="$slots['freelancer-signup-step-footer-left']" name="freelancer-signup-step-footer-left" />
                        <VJoyButton
                            v-if="!$slots['freelancer-signup-step-footer-left'] && isCtaBackVisible && couldGoBack"
                            data-testid="freelancer-signup-main-page-cta-back"
                            :label="$t('profile.signup.step.generic.cta.back')"
                            size="small"
                            variant="secondary"
                            :disabled="!isCtaBackEnabled"
                            @click="handleClickOnCtaBack"
                        />
                    </template>
                    <template #footer-right>
                        <VJoyButton
                            v-if="props.isCtaSkipVisible"
                            data-testid="freelancer-signup-main-page-cta-skip"
                            :label="$t('profile.signup.step.generic.cta.skip')"
                            size="small"
                            variant="ghost"
                            :disabled="isStepLoading"
                            @click="handleClickOnCtaSkip"
                        />
                        <template v-if="props.isCtaNextVisible">
                            <VJoyButton
                                v-if="isLastStep"
                                data-testid="freelancer-signup-main-page-cta-finish"
                                :label="finishCtaLabel"
                                size="small"
                                variant="main"
                                :loading="isSaving"
                                :disabled="!isCtaNextEnabled || isStepLoading || isSaving"
                                @click="handleClickOnCtaFinish"
                            />
                            <VJoyButton
                                v-else
                                data-testid="freelancer-signup-main-page-cta-next"
                                :label="ctaNextLabel"
                                size="small"
                                variant="main"
                                :loading="isSaving"
                                :disabled="!isCtaNextEnabled || isStepLoading || isSaving"
                                @click="handleClickOnCtaNext"
                            />
                        </template>
                    </template>
                </VJoyFunnelFooter>
            </div>
        </template>
        <template #template-loader-content>
            <slot v-if="hasSlotLoader" name="freelancer-signup-step-loader" />
            <VJoyScreenLoader v-else data-testid="freelancer-signup-step-loader" />
        </template>
    </VJoyTemplate>
</template>
<script setup lang="ts">
    import {computed, nextTick, onMounted, onUnmounted, ref, type SetupContext, toRef, useAttrs, useSlots} from 'vue';

    const TEXT_INPUT_TYPES = new Set(['text', 'email', 'password', 'tel', 'number', 'search', 'url']);
    import {storeToRefs} from 'pinia';
    import {useLogger, useTranslation} from '#imports';
    import {useEventBus} from '@malt/utils';
    import {pushVJoySnackbar, VJoyButton, VJoyFunnelFooter, VJoyScreenLoader, VJoyTemplate} from '@maltjoy/core-vue';

    import {useScrollToElement} from '~/composables/ui/useScrollToElement';
    import {freelancerSignupEventBus} from '~/config/freelancer-signup-events';
    import {useFreelancerSignupNavigation} from '~/features/freelancer-signup/navigation/composables/useFreelancerSignupNavigation';
    import {useFreelancerSignupNavigationStore} from '~/features/freelancer-signup/navigation/store/FreelancerSignupNavigationStore';
    import {useFreelancerSignupTracking} from '~/features/freelancer-signup/track/composables/useFreelancerSignupTracking';
    import {FreelancerSignupTrackingActionContextStepSaved} from '~/features/freelancer-signup/track/types/FreelancerSignupTracking';
    import {useFreelancerSignupFunctionalError} from '~/features/freelancer-signup/workflow/composables/useFreelancerSignupFunctionalError';
    import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
    import {FreelancerSignupContext} from '../../_common/types/FreelancerSignupContext';

    type FreelancerSignupTrackedEvents = 'saved' | 'skipped' | 'viewed';

    // Stores
    const navigationStore = useFreelancerSignupNavigationStore();
    const freelancerSignupStore = useFreelancerSignupStore();
    const {currentStep, funnelContext, isLastStep} = storeToRefs(navigationStore);

    // Composable
    const slots = useSlots();
    const {scrollToTop} = useScrollToElement();
    const freelancerSignupNavigation = useFreelancerSignupNavigation();
    const track = useFreelancerSignupTracking();
    const {isErrorRequiringToRedirect, isErrorWithFunctionalErrorCode, askForRedirectionIfNeeded} = useFreelancerSignupFunctionalError();
    const logger = useLogger();
    const pageAttrs: SetupContext['attrs'] = useAttrs();
    const {t} = useTranslation();

    // Props
    const props = withDefaults(
        defineProps<{
            dataTestid: string;
            /**
             * Optional function to save current step data.
             * Triggered by following event(s):  freelancer-signup-step:go-next
             * Triggered by following action(s): click on save
             */
            saveStep?: () => Promise<boolean>;
            skipStep?: () => Promise<boolean>;
            titleColor?: 'primary' | 'neutral';
            isStepLoading?: boolean;
            isCtaNextEnabled?: boolean;
            isCtaNextVisible?: boolean;
            ctaNextLabel?: string;
            isCtaBackVisible?: boolean;
            isCtaSkipVisible?: boolean;
            trackingIgnoreEvents?: FreelancerSignupTrackedEvents[];
            trackingStepSavedActionContext?: FreelancerSignupTrackingActionContextStepSaved;
        }>(),
        {
            titleColor: 'primary',
            saveStep: undefined,
            skipStep: undefined,
            isCtaNextEnabled: true,
            isCtaNextVisible: true,
            isCtaBackVisible: true,
            isCtaSkipVisible: false,
            trackingIgnoreEvents: () => [],
            trackingStepSavedActionContext: FreelancerSignupTrackingActionContextStepSaved.NONE,
        },
    );

    // Refs
    const templateElementRef = ref();
    const isCtaNextEnabled = toRef(props, 'isCtaNextEnabled');
    const isStepLoading = toRef(props, 'isStepLoading');
    const isSaving = ref(false);

    // Computed
    const hasSlotLoader = computed((): boolean => slots['freelancer-signup-step-loader'] !== undefined);
    const isCtaBackEnabled = computed((): boolean => !isStepLoading.value && !isSaving.value);
    const couldGoBack = computed(() => currentStep.value.previousEnabled);
    const cssTitleBackgroundColor = computed(() => (props.titleColor === 'neutral' ? 'var(--joy-color-neutral-30)' : 'var(--joy-color-primary-30)'));
    const cssTitleColor = computed(() => (props.titleColor === 'neutral' ? 'var(--joy-color-neutral-60)' : 'var(--joy-color-neutral-0)'));
    const isTransitionInProgress = computed(() =>
        Object.prototype.hasOwnProperty.call(pageAttrs, 'is-transition-finished') ? !pageAttrs['is-transition-finished'] : false,
    );
    const finishCtaLabel = computed(() =>
        funnelContext.value !== FreelancerSignupContext.STRATEGY
            ? t('profile.signup.step.generic.cta.finish')
            : t('profile.signup.step.generic.cta.submit'),
    );
    const ctaNextLabel = computed(() => props.ctaNextLabel ?? t('profile.signup.step.generic.cta.next'));

    // Hooks
    useEventBus(freelancerSignupEventBus, 'freelancer-signup-step:go-next', handleClickOnCtaNext);
    useEventBus(freelancerSignupEventBus, 'freelancer-signup-step:go-to-first-step-to-complete', handleGoToFirstStepToComplete);
    useEventBus(freelancerSignupEventBus, 'freelancer-signup-step:go-to-signin', handleGoToSignin);
    useEventBus(freelancerSignupEventBus, 'freelancer-signup-step:go-to-welcome-back', handleGoToWelcomeBack);
    useEventBus(freelancerSignupEventBus, 'freelancer-signup-step:go-to-regular-workflow', handleGoToRegularWorkflow);

    onMounted(() => {
        if (!_isIgnoreEventOfType('viewed')) {
            track.stepViewed();
        }
        document.addEventListener('keydown', handleEnterKey);
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleEnterKey);
    });

    // Event handlers
    async function handleClickOnCtaBack(): Promise<void> {
        if (couldGoBack.value) {
            await freelancerSignupNavigation.previous();
        }
    }

    async function handleClickOnCtaNext(): Promise<void> {
        await _saveOrSkipAndPerformAction(props.saveStep, async () => {
            if (!_isIgnoreEventOfType('saved')) {
                track.stepSaved({actionContext: props.trackingStepSavedActionContext});
            }
            await freelancerSignupNavigation.next();
        });
    }

    async function handleClickOnCtaFinish(): Promise<void> {
        await _saveOrSkipAndPerformAction(
            props.saveStep,
            async () => {
                await freelancerSignupStore.finishWorkflow();
                await freelancerSignupNavigation.redirectOnWorkflowEnd();
            },
            0,
        );
    }

    async function handleClickOnCtaSkip(): Promise<void> {
        await _saveOrSkipAndPerformAction(props.skipStep, async () => {
            if (!_isIgnoreEventOfType('skipped')) {
                track.stepSkipped();
            }
            await freelancerSignupNavigation.next();
        });
    }

    async function handleGoToFirstStepToComplete(payload: {save: boolean} | void): Promise<void> {
        if (payload?.save) {
            await _saveOrSkipAndPerformAction(props.saveStep, async () => {
                await freelancerSignupNavigation.goToFirstStepToComplete();
            });
        } else {
            // Ensure workflow has been updated since we received this event
            await nextTick(async () => {
                await freelancerSignupNavigation.goToFirstStepToComplete();
            });
        }
    }

    async function handleGoToSignin(): Promise<void> {
        await freelancerSignupNavigation.goToSigninWithRedirectionToFunnel();
    }

    async function handleGoToWelcomeBack(): Promise<void> {
        await freelancerSignupNavigation.goToWelcomeBack();
    }

    async function handleGoToRegularWorkflow(): Promise<void> {
        await freelancerSignupNavigation.goToRegularWorkflow();
    }

    function handleEnterKey(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;

        const target = event.target as HTMLInputElement;
        if (target.tagName.toLowerCase() !== 'input') return;
        if (!TEXT_INPUT_TYPES.has(target.type.toLowerCase())) return;

        // Skip combobox/autocomplete inputs (e.g. job title, skills typeahead)
        if (target.getAttribute('aria-autocomplete') || target.closest('[role="combobox"]')) return;

        // Skip if a dropdown is open (check computed style, not [hidden] attr, to handle v-show-hidden listboxes)
        const hasOpenListbox = Array.from(document.querySelectorAll('[role="listbox"]')).some(
            (el) => getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden',
        );
        if (hasOpenListbox) return;

        if (!isCtaNextEnabled.value || !props.isCtaNextVisible || isSaving.value || isStepLoading.value) return;

        event.preventDefault();
        if (isLastStep.value) {
            handleClickOnCtaFinish();
        } else {
            handleClickOnCtaNext();
        }
    }

    function _isIgnoreEventOfType(event: FreelancerSignupTrackedEvents): boolean {
        return props.trackingIgnoreEvents.includes(event);
    }

    async function _saveOrSkipAndPerformAction(
        saveOrSkip: (() => Promise<boolean>) | undefined,
        onSuccess: () => Promise<void>,
        delay: number = 200,
    ): Promise<void> {
        isSaving.value = true;

        try {
            const okToMove = saveOrSkip ? await saveOrSkip() : true;
            if (okToMove) {
                _scrollToTopIfPossible();
                await new Promise((resolve) => setTimeout(resolve, delay));
                await onSuccess();
            }
        } catch (error) {
            if (isErrorWithFunctionalErrorCode(error) && isErrorRequiringToRedirect(error)) {
                await askForRedirectionIfNeeded(error);
            } else {
                logger.error(error);
                pushVJoySnackbar({
                    props: {
                        message: t('profile.creation.common.notification.error.technical'),
                        level: 'error',
                    },
                });
            }
        } finally {
            isSaving.value = false;
        }
    }

    function _scrollToTopIfPossible(): void {
        if (templateElementRef.value && templateElementRef.value.$el) {
            scrollToTop(templateElementRef.value.$el);
        }
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .freelancer-signup-step {
        .signup-title {
            --title-background-color: v-bind(cssTitleBackgroundColor);
            --title-color: v-bind(cssTitleColor);

            @include text.getFontBySize(secondary-600);

            @include mq.screen_xs() {
                @include text.getFontBySize(secondary-400);
            }

            &--with-subtitle {
                margin-bottom: var(--joy-core-spacing-1);
            }

            span:not(.joy-icon) {
                border-radius: var(--joy-core-radius-5);
                padding: 2px var(--joy-core-spacing-3);
                background-color: var(--title-background-color);
                color: var(--title-color);
            }
        }
        .signup-title--with-subtitle {
            margin-bottom: 0;
        }

        .signup-subtitle {
            @include text.getFontBySize(primary-650);

            @include mq.screen_xs() {
                @include text.getFontBySize(primary-450);
            }
        }
    }

    .freelancer-signup-step {
        &__main {
            .xlarge-title .joy-panel__header-title.joy-panel__header-title--large {
                @include text.getFontBySize(secondary-200);
            }
        }
        &__wrapper {
            display: flex;
            flex-direction: row;
            gap: var(--joy-core-spacing-2);

            @include mq.screen_xs {
                flex-direction: column;
                align-items: center;
                gap: var(--joy-core-spacing-8);
            }
        }

        .joy-spinner {
            display: none;
        }

        .joy-screen-loader--light {
            background: none !important;
        }
        .freelancer-signup-email-info {
            word-break: normal;
            overflow-wrap: anywhere;
        }

        .joy-funnel-footer {
            @media screen and (max-width: 320px) {
                &__right {
                    flex-direction: column-reverse;
                    align-items: stretch;
                    gap: var(--joy-core-spacing-2);

                    .joy-button {
                        width: 100%;
                        margin: 0;
                    }
                }
            }
        }
    }
</style>
